import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEUR, formatApiError, API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import {
  Plus, FileText, Search, Copy, Send, Download, ArrowRightCircle,
  Edit3, Trash2, Mail, Filter
} from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import QuoteModal from "../components/QuoteModal";

const STATUSES = ["Bozza", "Inviato", "Accettato", "Rifiutato", "Scaduto"];
const STATUS_STYLE = {
  "Bozza":     { dot: "bg-slate-400",   text: "text-slate-300", bg: "bg-slate-500/10 border-slate-500/40" },
  "Inviato":   { dot: "bg-sky-400",     text: "text-sky-200",   bg: "bg-sky-500/10 border-sky-500/40" },
  "Accettato": { dot: "bg-emerald-400", text: "text-emerald-200", bg: "bg-emerald-500/15 border-emerald-500/50" },
  "Rifiutato": { dot: "bg-rose-400",    text: "text-rose-200",  bg: "bg-rose-500/10 border-rose-500/40" },
  "Scaduto":   { dot: "bg-amber-400",   text: "text-amber-200", bg: "bg-amber-500/10 border-amber-500/40" },
};

const fmtDate = (iso) => { if (!iso) return "-"; const [y,m,d] = iso.split("-"); return `${d}/${m}/${y}`; };

export default function Preventivi() {
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [quoteToOpen, setQuoteToOpen] = useState(null);
  const [sendTarget, setSendTarget] = useState(null); // quote da inviare
  const [sendTo, setSendTo] = useState("");
  const [sendMsg, setSendMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [q, c] = await Promise.all([api.get("/quotes"), api.get("/customers")]);
      setQuotes(q.data || []);
      setCustomers(c.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // (AI Agent) Auto-apre il preventivo passato via ?open=<quote_id>
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get("open");
    if (!id || loading || quotes.length === 0) return;
    const q = quotes.find((x) => x.id === id);
    if (q) {
      setQuoteToOpen(q);
      setShowModal(true);
      // pulisci la query per evitare riapertura al refresh
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, quotes]);

  const custMap = useMemo(() => Object.fromEntries(customers.map(c => [c.id, c])), [customers]);

  const filtered = useMemo(() => {
    const s = (search || "").toLowerCase().trim();
    return quotes.filter(q => {
      if (statusFilter !== "all" && (q.status || "Bozza") !== statusFilter) return false;
      if (!s) return true;
      const custName = (custMap[q.customer_id]?.name || "").toLowerCase();
      return (q.quote_number || "").toLowerCase().includes(s)
        || custName.includes(s)
        || (q.issue_date || "").includes(s);
    });
  }, [quotes, custMap, search, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: quotes.length };
    for (const s of STATUSES) c[s] = 0;
    for (const q of quotes) { const st = q.status || "Bozza"; c[st] = (c[st] || 0) + 1; }
    return c;
  }, [quotes]);

  const totalFiltered = useMemo(() => filtered.reduce((a, q) => a + Number(q.total || 0), 0), [filtered]);

  const openNew = () => { setQuoteToOpen(null); setShowModal(true); };
  const openEdit = (q) => { setQuoteToOpen(q); setShowModal(true); };

  const duplicate = async (q) => {
    try {
      await api.post(`/quotes/${q.id}/duplicate`);
      toast.success("Preventivo duplicato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Eliminare il preventivo ${q.quote_number}?`)) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      toast.success("Preventivo eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const convertToBooking = async (q) => {
    if (!window.confirm(`Convertire ${q.quote_number} in prenotazioni? Verrà creata una prenotazione per ogni riga del preventivo.`)) return;
    try {
      const r = await api.post(`/quotes/${q.id}/convert-to-booking`);
      if (r.data?.already_converted) {
        toast.info("Il preventivo è già stato convertito");
      } else {
        toast.success(`${r.data?.count || 0} prenotazione/i create. Status → Accettato`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openSend = (q) => {
    setSendTarget(q);
    setSendTo(custMap[q.customer_id]?.email || "");
    setSendMsg("");
  };
  const doSend = async () => {
    if (!sendTo.trim()) { toast.error("Indica un indirizzo email destinatario"); return; }
    setSending(true);
    try {
      await api.post(`/quotes/${sendTarget.id}/send-email`, { to: sendTo.trim(), message: sendMsg.trim() });
      toast.success(`Email inviata a ${sendTo}`);
      setSendTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="preventivi-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-white tracking-tight">Preventivi</h1>
          <p className="text-sm text-slate-500 mt-1">Hub centralizzato per creazione, invio e conversione dei preventivi NCC.</p>
        </div>
        <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="new-quote-global-btn">
          <Plus size={16}/> Nuovo preventivo
        </Button>
      </div>

      {/* Filtri stato + ricerca */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={()=>setStatusFilter("all")}
          data-testid="quotes-filter-all"
          className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-mono-num font-bold border ${statusFilter==="all" ? "bg-amber-500 text-slate-950 border-amber-500" : "bg-transparent text-slate-400 border-slate-800 hover:border-slate-600"}`}>
          Tutti · {counts.all}
        </button>
        {STATUSES.map(s => (
          <button key={s}
            onClick={()=>setStatusFilter(s)}
            data-testid={`quotes-filter-${s.toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-mono-num font-bold border inline-flex items-center gap-1.5 ${statusFilter===s ? "bg-amber-500 text-slate-950 border-amber-500" : `${STATUS_STYLE[s].bg} ${STATUS_STYLE[s].text} border`}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_STYLE[s].dot}`}/> {s} · {counts[s] || 0}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
          <Input placeholder="Cerca per cliente, numero, data…" value={search} onChange={e=>setSearch(e.target.value)} className="pl-10 bg-slate-900 border-slate-800 w-full sm:w-80" data-testid="quotes-search"/>
        </div>
      </div>

      {/* Totale filtrato */}
      <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent px-4 py-3 flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-amber-300 font-mono-num font-bold">Totale filtrato</div>
        <div className="text-2xl font-mono-num font-extrabold text-amber-300" data-testid="quotes-total-filtered">{fmtEUR(totalFiltered)}</div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-800 bg-slate-950/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="p-3 text-left">N°</th>
                <th className="p-3 text-left">Cliente</th>
                <th className="p-3 text-left">Emissione</th>
                <th className="p-3 text-left">Validità</th>
                <th className="p-3 text-right">Totale</th>
                <th className="p-3 text-center">Stato</th>
                <th className="p-3 text-right pr-4">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {loading && <tr><td colSpan={7} className="p-10 text-center text-slate-500">Caricamento…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="p-14 text-center text-slate-500 italic">
                  {quotes.length === 0 ? "Nessun preventivo ancora. Clicca 'Nuovo preventivo' per iniziare." : "Nessun preventivo con questi filtri."}
                </td></tr>
              )}
              {filtered.map(q => {
                const st = STATUS_STYLE[q.status || "Bozza"] || STATUS_STYLE["Bozza"];
                const cust = custMap[q.customer_id];
                return (
                  <tr key={q.id} className="hover:bg-slate-900/40 transition-colors" data-testid={`quote-row-${q.id}`}>
                    <td className="p-3 font-mono-num text-amber-400 text-xs font-bold">{q.quote_number || "—"}</td>
                    <td className="p-3">
                      <div className="text-white font-medium">{cust?.name || "—"}</div>
                      {cust?.email && <div className="text-[11px] text-slate-500">{cust.email}</div>}
                    </td>
                    <td className="p-3 font-mono-num text-xs text-slate-300">{fmtDate(q.issue_date)}</td>
                    <td className="p-3 font-mono-num text-xs text-slate-400">{fmtDate(q.validity_date)}</td>
                    <td className="p-3 text-right font-mono-num font-bold text-white">{fmtEUR(q.total)}</td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-mono-num font-bold border ${st.bg} ${st.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}/> {q.status || "Bozza"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <button title="Modifica" onClick={()=>openEdit(q)} className="p-1.5 text-slate-400 hover:text-amber-400 rounded" data-testid={`quote-edit-${q.id}`}><Edit3 size={14}/></button>
                        <a href={`${API_BASE}/quotes/${q.id}/pdf-premium`} target="_blank" rel="noreferrer" title="Scarica PDF Premium" className="p-1.5 text-slate-400 hover:text-amber-400 rounded" data-testid={`quote-pdf-premium-${q.id}`}><Download size={14}/></a>
                        <button title="Invia email" onClick={()=>openSend(q)} className="p-1.5 text-slate-400 hover:text-emerald-400 rounded" data-testid={`quote-send-${q.id}`}><Mail size={14}/></button>
                        <button title="Duplica" onClick={()=>duplicate(q)} className="p-1.5 text-slate-400 hover:text-indigo-400 rounded" data-testid={`quote-dup-${q.id}`}><Copy size={14}/></button>
                        {(q.status === "Accettato" || q.status === "Inviato") && (
                          <button title="Converti in prenotazione" onClick={()=>convertToBooking(q)} className="p-1.5 text-emerald-400 hover:text-emerald-300 rounded" data-testid={`quote-convert-${q.id}`}><ArrowRightCircle size={14}/></button>
                        )}
                        <button title="Elimina" onClick={()=>remove(q)} className="p-1.5 text-slate-500 hover:text-rose-400 rounded" data-testid={`quote-delete-${q.id}`}><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal nuovo/edit preventivo */}
      {showModal && (
        <QuoteModal
          customer={null}
          quoteToOpen={quoteToOpen}
          customersList={customers}
          onClose={(changed)=>{ setShowModal(false); setQuoteToOpen(null); if (changed) load(); }}
        />
      )}

      {/* Modal invio email */}
      {sendTarget && (
        <Dialog open onOpenChange={(o)=>!o && setSendTarget(null)}>
          <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-xl text-white">Invia preventivo · <span className="text-amber-400 font-mono-num">{sendTarget.quote_number}</span></DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-widest font-mono-num">Email destinatario</label>
                <Input type="email" value={sendTo} onChange={e=>setSendTo(e.target.value)} className="bg-slate-900 border-slate-800" placeholder="cliente@example.com" data-testid="send-quote-email"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-widest font-mono-num">Messaggio (opzionale)</label>
                <Textarea rows={4} value={sendMsg} onChange={e=>setSendMsg(e.target.value)} className="bg-slate-900 border-slate-800" placeholder="Lasciare vuoto per usare il testo di default."/>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={()=>setSendTarget(null)}>Annulla</Button>
              <Button onClick={doSend} disabled={sending} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold" data-testid="send-quote-confirm">
                {sending ? "Invio in corso…" : (<><Send size={14}/> Invia</>)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
